/**
 * helpers.js
 * Fonctions utilitaires pour le simulateur CAE
 */

// ========== TAUX DE TVA PAR MÉTIER ==========
const TVA_RATES = {
  "Commerce (achat, revente)": 0.20,
  "Bâtiment, travaux": 0.10, // TVA réduite
  "Création artisanale": 0.20,
  "Production alimentaire, culinaire": 0.055, // TVA réduite alimentaire
  "Prestation de services (conseil, bureau d'études, communication, etc.)": 0.20,
  "Informatique, numérique": 0.20,
  "Formation professionnelle": 0, // Exonérée de TVA (organisme de formation déclaré)
  "Culturelle, artistique": 0.20,
  "Agriculture, paysage": 0.10,
  "Service à la personne": 0.10,
  "Bien-être, soin": 0.20,
};

// ========== FORMATAGE DES MONTANTS ==========

/**
 * Formate un montant en euros avec séparateurs français
 * @param {number} montant - Montant à formater
 * @param {number} decimales - Nombre de décimales (défaut: 2)
 * @returns {string} Montant formaté (ex: "1 234,56 €")
 */
export function formatEuro(montant, decimales = 2) {
  if (typeof montant !== 'number' || isNaN(montant)) {
    return '0,00 €';
  }

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(montant);
}

/**
 * Formate un pourcentage avec séparateur français
 * @param {number} valeur - Valeur en pourcentage (ex: 14 pour 14%)
 * @param {number} decimales - Nombre de décimales (défaut: 1)
 * @returns {string} Pourcentage formaté (ex: "13,5 %")
 */
export function formatPourcentage(valeur, decimales = 1) {
  if (typeof valeur !== 'number' || isNaN(valeur)) {
    return '0 %';
  }

  return new Intl.NumberFormat('fr-FR', {
    style: 'percent',
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(valeur / 100);
}

/**
 * Formate un nombre avec séparateurs de milliers
 * @param {number} nombre - Nombre à formater
 * @param {number} decimales - Nombre de décimales (défaut: 0)
 * @returns {string} Nombre formaté (ex: "1 234,56")
 */
export function formatNombre(nombre, decimales = 0) {
  if (typeof nombre !== 'number' || isNaN(nombre)) {
    return '0';
  }

  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(nombre);
}

// ========== CALCUL DES CHARGES ==========

/**
 * Calcule le total des charges professionnelles
 * @param {object} charges - Objet contenant toutes les charges
 * @returns {number} Total des charges
 */
export function calculateChargesTotal(charges) {
  if (!charges || typeof charges !== 'object') {
    return 0;
  }

  // Liste de toutes les clés de charges possibles
  const clesPossibles = [
    'matieres_premieres',
    'sous_traitance',
    'loyer',
    'internet_telephone',
    'indemnites_km',
    'deplacements',
    'logiciel_compta',
    'communication_pub',
    'consommables',
    'repas_affaires',
    'assurance_rcp',
    'assurance_decennale',
    'compte_bancaire',
    'comptable',
    'cet_cvae_cfe',
    'mutuelle_prevoyance',
    'label_bio',
    'code_barres',
    'logiciel_formation',
    'frais_professionnels_total',
  ];

  // Si frais_professionnels_total existe, on l'utilise directement
  if (charges.frais_professionnels_total !== undefined) {
    const matieres = charges.matieres_premieres || 0;
    const soustraitance = charges.sous_traitance || 0;
    const frais = charges.frais_professionnels_total || 0;
    return matieres + soustraitance + frais;
  }

  // Sinon, on additionne toutes les charges
  let total = 0;
  for (const cle of clesPossibles) {
    const valeur = charges[cle];
    if (typeof valeur === 'number' && !isNaN(valeur)) {
      total += valeur;
    }
  }

  return total;
}

/**
 * Calcule le total des frais professionnels (hors matières et sous-traitance)
 * @param {object} charges - Objet contenant toutes les charges
 * @returns {number} Total des frais professionnels
 */
export function calculateFraisProfessionnels(charges) {
  if (!charges || typeof charges !== 'object') {
    return 0;
  }

  // Exclure matières premières et sous-traitance
  const clesFrais = [
    'loyer',
    'internet_telephone',
    'indemnites_km',
    'deplacements',
    'logiciel_compta',
    'communication_pub',
    'consommables',
    'repas_affaires',
    'assurance_rcp',
    'assurance_decennale',
    'compte_bancaire',
    'comptable',
    'cet_cvae_cfe',
    'mutuelle_prevoyance',
    'label_bio',
    'code_barres',
    'logiciel_formation',
  ];

  let total = 0;
  for (const cle of clesFrais) {
    const valeur = charges[cle];
    if (typeof valeur === 'number' && !isNaN(valeur)) {
        if (cle === 'internet_telephone' && valeur > 0) {
          // Appliquer une prise en charge de 50% pour internet et téléphone
          total += valeur * 0.5;
        } else {
          total += valeur;
        }
    }
  }

  return total;
}

// ========== CONVERSION HT/TTC ==========

/**
 * Convertit un montant HT en TTC selon le métier
 * @param {number} montantHT - Montant hors taxes
 * @param {string} metier - Métier exercé
 * @param {Object} [configMetiers] - Config métiers par CAE (config.metiers)
 * @returns {number} Montant TTC
 */
export function convertHTtoTTC(montantHT, metier, configMetiers) {
  if (typeof montantHT !== 'number' || isNaN(montantHT) || montantHT < 0) {
    return 0;
  }

  const tauxTVA = getTauxTVA(metier, configMetiers);
  return montantHT * (1 + tauxTVA);
}

/**
 * Convertit un montant TTC en HT selon le métier
 * @param {number} montantTTC - Montant toutes taxes comprises
 * @param {string} metier - Métier exercé
 * @param {Object} [configMetiers] - Config métiers par CAE (config.metiers)
 * @returns {number} Montant HT
 */
export function convertTTCtoHT(montantTTC, metier, configMetiers) {
  if (typeof montantTTC !== 'number' || isNaN(montantTTC) || montantTTC < 0) {
    return 0;
  }

  const tauxTVA = getTauxTVA(metier, configMetiers);
  return montantTTC / (1 + tauxTVA);
}

/**
 * Récupère le taux de TVA pour un métier donné.
 * Priorité au taux défini dans la config CAE (config.metiers[metier].taux_tva),
 * sinon repli sur le barème figé TVA_RATES, sinon 20 %.
 * @param {string} metier - Métier exercé
 * @param {Object} [configMetiers] - Config métiers par CAE (config.metiers)
 * @returns {number} Taux de TVA (ex: 0.20 pour 20%)
 */
export function getTauxTVA(metier, configMetiers) {
  if (!metier || typeof metier !== 'string') {
    return 0.20; // Taux normal par défaut
  }

  const tauxConfig = configMetiers?.[metier]?.taux_tva;
  if (typeof tauxConfig === 'number') {
    return tauxConfig;
  }

  return TVA_RATES[metier] ?? 0.20;
}

/**
 * Calcule le montant de TVA
 * @param {number} montantHT - Montant hors taxes
 * @param {string} metier - Métier exercé
 * @param {Object} [configMetiers] - Config métiers par CAE (config.metiers)
 * @returns {number} Montant de TVA
 */
export function calculateTVA(montantHT, metier, configMetiers) {
  if (typeof montantHT !== 'number' || isNaN(montantHT) || montantHT < 0) {
    return 0;
  }

  const tauxTVA = getTauxTVA(metier, configMetiers);
  return montantHT * tauxTVA;
}

// ========== VALIDATION DES ENTRÉES ==========

/**
 * Valide qu'une valeur est un nombre dans une plage donnée
 * @param {any} value - Valeur à valider
 * @param {number} min - Valeur minimum (incluse)
 * @param {number} max - Valeur maximum (incluse)
 * @returns {boolean} true si valide, false sinon
 */
export function validateInput(value, min = 0, max = Infinity) {
  // Vérifier que c'est un nombre
  const nombre = Number(value);
  
  if (isNaN(nombre)) {
    return false;
  }

  // Vérifier la plage
  return nombre >= min && nombre <= max;
}

/**
 * Valide un chiffre d'affaires
 * @param {number} ca - Chiffre d'affaires
 * @param {number} max - CA maximum autorisé (défaut: 500000)
 * @returns {object} {valid: boolean, error: string}
 */
export function validateCA(ca, max = 500000) {
  if (!ca && ca !== 0) {
    return { valid: false, error: 'Le chiffre d\'affaires est requis' };
  }

  const nombre = Number(ca);

  if (isNaN(nombre)) {
    return { valid: false, error: 'Le chiffre d\'affaires doit être un nombre' };
  }

  if (nombre < 0) {
    return { valid: false, error: 'Le chiffre d\'affaires doit être positif' };
  }

  if (nombre > max) {
    return { valid: false, error: `Le chiffre d'affaires ne peut pas dépasser ${formatEuro(max, 0)}` };
  }

  return { valid: true, error: null };
}

/**
 * Valide un montant de charges
 * @param {number} charges - Montant des charges
 * @param {number} ca - CA pour vérifier que charges < CA
 * @returns {object} {valid: boolean, error: string}
 */
export function validateCharges(charges, ca = null) {
  if (charges === undefined || charges === null) {
    return { valid: true, error: null }; // Charges optionnelles
  }

  const nombre = Number(charges);

  if (isNaN(nombre)) {
    return { valid: false, error: 'Les charges doivent être un nombre' };
  }

  if (nombre < 0) {
    return { valid: false, error: 'Les charges doivent être positives' };
  }

  if (ca !== null && nombre > ca) {
    return { valid: false, error: 'Les charges ne peuvent pas dépasser le chiffre d\'affaires' };
  }

  return { valid: true, error: null };
}

/**
 * Valide une adresse email
 * @param {string} email - Email à valider
 * @returns {boolean} true si valide, false sinon
 */
export function validateEmail(email) {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Regex simple pour validation email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// ========== ARRONDI ==========

/**
 * Arrondit un nombre à un certain nombre de décimales
 * @param {number} nombre - Nombre à arrondir
 * @param {number} decimales - Nombre de décimales (défaut: 2)
 * @returns {number} Nombre arrondi
 */
export function arrondir(nombre, decimales = 2) {
  if (typeof nombre !== 'number' || isNaN(nombre)) {
    return 0;
  }

  const multiplicateur = Math.pow(10, decimales);
  return Math.round(nombre * multiplicateur) / multiplicateur;
}

/**
 * Arrondit à l'euro supérieur
 * @param {number} nombre - Nombre à arrondir
 * @returns {number} Nombre arrondi à l'euro supérieur
 */
export function arrondiEuroSup(nombre) {
  if (typeof nombre !== 'number' || isNaN(nombre)) {
    return 0;
  }

  return Math.ceil(nombre);
}

/**
 * Arrondit à l'euro inférieur
 * @param {number} nombre - Nombre à arrondir
 * @returns {number} Nombre arrondi à l'euro inférieur
 */
export function arrondiEuroInf(nombre) {
  if (typeof nombre !== 'number' || isNaN(nombre)) {
    return 0;
  }

  return Math.floor(nombre);
}

// ========== UTILITAIRES DIVERS ==========

/**
 * Nettoie une valeur d'input (supprime espaces, convertit virgules en points)
 * @param {string} value - Valeur à nettoyer
 * @returns {number} Nombre nettoyé
 */
export function cleanInputValue(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  // Convertir en string si ce n'est pas déjà le cas
  const str = String(value);

  // Supprimer les espaces et remplacer virgules par points
  const cleaned = str.replace(/\s/g, '').replace(',', '.');

  // Convertir en nombre
  const nombre = Number(cleaned);

  return isNaN(nombre) ? 0 : nombre;
}

/**
 * Vérifie si un objet de charges est vide
 * @param {object} charges - Objet de charges
 * @returns {boolean} true si vide, false sinon
 */
export function isChargesEmpty(charges) {
  if (!charges || typeof charges !== 'object') {
    return true;
  }

  const total = calculateChargesTotal(charges);
  return total === 0;
}

/**
 * Clone profondément un objet (pour éviter les mutations)
 * @param {object} obj - Objet à cloner
 * @returns {object} Objet cloné
 */
export function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  return JSON.parse(JSON.stringify(obj));
}

/**
 * Calcule le pourcentage d'un montant
 * @param {number} montant - Montant de base
 * @param {number} pourcentage - Pourcentage à calculer
 * @returns {number} Résultat du calcul
 */
export function calculerPourcentage(montant, pourcentage) {
  if (typeof montant !== 'number' || typeof pourcentage !== 'number') {
    return 0;
  }

  return (montant * pourcentage) / 100;
}

/**
 * Retourne le minimum de plusieurs nombres
 * @param  {...number} nombres - Nombres à comparer
 * @returns {number} Minimum
 */
export function min(...nombres) {
  const valides = nombres.filter(n => typeof n === 'number' && !isNaN(n));
  return valides.length > 0 ? Math.min(...valides) : 0;
}

/**
 * Retourne le maximum de plusieurs nombres
 * @param  {...number} nombres - Nombres à comparer
 * @returns {number} Maximum
 */
export function max(...nombres) {
  const valides = nombres.filter(n => typeof n === 'number' && !isNaN(n));
  return valides.length > 0 ? Math.max(...valides) : 0;
}

// ========== CALCUL DES CHARGES PAR CATÉGORIE (MICRO / SASU / EI) ==========

const MIN_CHARGES_STRUCTURE = {
  logiciel_compta: 20,
  logiciel_formation: 200,
  compte_bancaire: 10,
  assurance_rcp: 15,
  assurance_decennale: 250,
  comptable: 250,            
  cet_cvae_cfe: 27,          
  mutuelle_prevoyance: 75,   
};

/**
 * Calcule les 4 catégories de charges pour un statut hors-CAE (MICRO, SASU, EI)
 * @param {object} charges - Charges saisies par l'utilisateur (champs individuels)
 * @param {string} metier - Métier exercé
 * @param {string} statut - 'MICRO', 'SASU' ou 'EI'
 * @param {number} ca_ht - CA HT mensuel (pour logiciel_formation = CA × 3%)
 * @returns {object} { matieres_et_sous_traitance, frais_participation_coop, autres_frais_structure, frais_fonctionnement }
 */
export function calculateChargesParCategorie(charges, metier, statut, ca_ht = 0, isTTC = false) {
  const isBatiment    = metier === 'Bâtiment, travaux';
  const isAlimentaire = metier === 'Production alimentaire, culinaire';
  const isFormation   = metier === 'Formation professionnelle';
  const isMicro       = statut === 'MICRO';

  // Pour MICRO, les charges ne sont pas déductibles de TVA : si saisi en HT → TTC (×1.2)
  // Si déjà saisi en TTC (isTTC=true), pas de conversion
  const coeff = isMicro && !isTTC ? 1.2 : 1;
  const r2 = (n) => Math.round(n);

  // ── Catégorie 1 : Matières premières + sous-traitance ──
  // Pour bâtiment, si matières premières non saisies, défaut = 45% du CA HT
  const matieres_saisies = charges.matieres_premieres || 0;
  const matieres_effectives = matieres_saisies > 0
    ? matieres_saisies
    : (isBatiment ? ca_ht * 0.45 : 0);
  const matieres_et_sous_traitance = r2(
    (matieres_effectives + (charges.sous_traitance || 0)) * coeff
  );

  // ── Catégorie 4 : Frais de fonctionnement (opérationnel) ──
  // indemnites_km = barème kilométrique, pas un achat TTC → pas de coeff
  const frais_fonctionnement = r2(
    (
      (charges.loyer || 0) +
      (charges.internet_telephone || 0) * 0.5 +
      (charges.deplacements || 0) +
      (charges.communication_pub || 0) +
      (charges.consommables || 0) +
      (charges.repas_affaires || 0)
    ) * coeff
    + (charges.indemnites_km || 0)
  );

  // ── Catégorie 3 : Autres frais de structure (avec minimums) ──
  const M = MIN_CHARGES_STRUCTURE;

  // Charges soumises à TVA → coeff appliqué
  // Charges TVA-exempt (assurances, frais bancaires, taxes) → pas de coeff même en MICRO HT

  // Label bio — conditionnel alimentaire, minimum CAE rate si entry > 0
  const label_bio = isAlimentaire && (charges.label_bio || 0) > 0
    ? Math.max(charges.label_bio || 0, (160 + 180) / 12) * coeff
    : 0;

  // Codes barres — conditionnel alimentaire
  const code_barres = isAlimentaire ? (charges.code_barres || 0) * coeff : 0;

  // Logiciel formation — conditionnel formation, minimum CA × 3%
  const logiciel_formation = isFormation
    ? Math.max(charges.logiciel_formation || 0, M.logiciel_formation) * coeff
    : 0;

  // Logiciel de comptabilité (minimum 20) — soumis à TVA
  const logiciel_compta = Math.max(charges.logiciel_compta || 0, M.logiciel_compta) * coeff;

  // Compte bancaire (minimum 20) — services financiers exonérés de TVA
  const compte_bancaire = Math.max(charges.compte_bancaire || 0, M.compte_bancaire);

  // RCP — assurance, exonérée de TVA
  const rcp = isBatiment ? 0 : Math.max(charges.assurance_rcp || 0, M.assurance_rcp);

  // Décennale — assurance, exonérée de TVA
  const decennale = isBatiment
    ? Math.max(charges.assurance_decennale || 0, M.assurance_decennale)
    : 0;

  // Comptable — 0 pour MICRO (non applicable)
  const comptable = !isMicro
    ? Math.max(charges.comptable || 0, M.comptable) * coeff
    : 0;

  // CET/CVAE/CFE — taxes, pas de TVA
  const cet = Math.max(charges.cet_cvae_cfe || 0, M.cet_cvae_cfe);

  // Mutuelle/prévoyance — assurance, exonérée de TVA
  const mutuelle = Math.max(charges.mutuelle_prevoyance || 0, M.mutuelle_prevoyance);

  const autres_frais_structure = r2(
    label_bio + code_barres + logiciel_formation + logiciel_compta +
    compte_bancaire + rcp + decennale + comptable + cet + mutuelle
  );

  return {
    matieres_et_sous_traitance,
    frais_participation_coop: 0,  // toujours 0 pour les statuts hors CAE
    autres_frais_structure,
    frais_fonctionnement,
  };
}

// Export par défaut de toutes les fonctions
export default {
  formatEuro,
  formatPourcentage,
  formatNombre,
  calculateChargesTotal,
  calculateFraisProfessionnels,
  calculateChargesParCategorie,
  convertHTtoTTC,
  convertTTCtoHT,
  getTauxTVA,
  calculateTVA,
  validateInput,
  validateCA,
  validateCharges,
  validateEmail,
  arrondir,
  arrondiEuroSup,
  arrondiEuroInf,
  cleanInputValue,
  isChargesEmpty,
  deepClone,
  calculerPourcentage,
  min,
  max,
};
